import React, { useState, useMemo, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { 
    Receipt, Truck, Package, Calendar, 
    User, Warehouse, CheckCircle2, 
    ShieldAlert, AlertTriangle, 
    RefreshCcw, Info, Printer, Copy
} from "lucide-react";
import { useOrderStore } from "../../../store/orderStore";
import { toast } from "react-toastify";
import { createCargoForOrder, checkCargoStatus } from "../../../service/cargo";
import { IconTextButton, TextButton } from "../../../components/ButtonComponents";

interface OrderDetailDialogProps {
    isOpen: boolean;
    onClose: () => void;
    order: any;
}

export const OrderDetailDialog = ({ isOpen, onClose, order: initialOrder }: OrderDetailDialogProps) => {
    const store = useOrderStore() as any;
    const [statusLoading, setStatusLoading] = useState(false);
    const [cargoLoading, setCargoLoading] = useState(false);
    
    // 1. LOCAL STATE: Props'tan gelen veriyi state'e alıyoruz ki güncellemeler anlık yansısın
    const [order, setOrder] = useState(initialOrder);

    // Dialog her açıldığında veya initialOrder değiştiğinde local state'i güncelle
    useEffect(() => {
        setOrder(initialOrder);
    }, [initialOrder]);

    // Tüm Statü Seçenekleri Tanımı
    const allOptions = [
        { name: "Beklemede", color: "#94a3b8", triggerAction: "NONE" },
        { name: "Onaylandı", color: "#4CAF50", triggerAction: "APPROVE_RECORD" },
        { name: "Hazırlanıyor", color: "#FF9800", triggerAction: "NONE" },
        { name: "Kargoya Verildi", color: "#2196F3", triggerAction: "NONE" },
        { name: "Tamamlandı", color: "#2E7D32", triggerAction: "NONE" },
        { name: "İptal Edildi", color: "#F44336", triggerAction: "NONE" },
    ];

    // Silsile Yoluyla Filtreleme (Workflow Logic)
    const filteredOptions = useMemo(() => {
        if (!order) return allOptions;
        const currentStatus = order.statuses?.[0]?.name;

        if (currentStatus === "Onaylandı" || currentStatus === "Hazırlanıyor") {
            return allOptions.filter(opt => ["Hazırlanıyor", "Kargoya Verildi", "Tamamlandı", "İptal Edildi"].includes(opt.name));
        }
        if (currentStatus === "Kargoya Verildi") {
            return allOptions.filter(opt => ["Tamamlandı", "İptal Edildi"].includes(opt.name));
        }
        if (currentStatus === "Tamamlandı" || currentStatus === "İptal Edildi") {
            return []; 
        }
        return allOptions;
    }, [order]);

    // Dropdown Seçili Gelme Mantığı
    const currentStatusValue = useMemo(() => {
        if (!order?.statuses?.[0]) return null;
        return allOptions.find(opt => opt.name === order.statuses[0].name) || null;
    }, [order]);

    const hasInvoice = order?.accountMovements?.some((m: any) => m.type === "INVOICE");
    const invoiceInfo = order?.accountMovements?.find((m: any) => m.type === "INVOICE");

    const totalRiskLimit = (order?.account?.riskLimit || 0) + (order?.account?.temporaryLimit || 0);
    const balance = order?.account?.balance || 0;
    const orderAmount = order?.totalAmount || 0;
    const isOverLimit = order?.account?.riskLimitType !== "UNLIMITED" && (balance + orderAmount) > totalRiskLimit;

    const handleUpdateStatus = async (selectedStatus: any) => {
        if (!selectedStatus) return;
        setStatusLoading(true);
        try {
            const cleanPayload = {
                accountId: order.accountId,
                warehouseId: order.warehouseId,
                orderDate: order.orderDate,
                deliveryDate: order.deliveryDate,
                totalAmount: order.totalAmount,
                currency: order.currency,
                description: order.description,
                status: {
                    name: selectedStatus.name,
                    color: selectedStatus.color,
                    triggerAction: selectedStatus.triggerAction || "NONE",
                    targetType: "ORDER"
                },
                statusDescription: `${selectedStatus.name} aşamasına web panel üzerinden geçildi.`,
                items: order.items.map((item: any) => ({
                    stockId: item.stockId,
                    variantId: item.variantId || null,
                    quantity: item.quantity,
                    price: item.price,
                    taxRate: item.taxRate,
                    taxAmount: item.taxAmount || 0,
                    discountRate: item.discountRate || 0,
                    discountAmount: item.discountAmount || 0,
                    total: item.total
                }))
            };

            // API Güncellemesi
            const updatedData = await store.updateItem(order.id, cleanPayload);
            
            // Local state'i API'den dönen yeni veriyle besle (Arayüz anında güncellenir)
            setOrder(updatedData);
            
            toast.success(`Durum Güncellendi: ${selectedStatus.name}`);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Durum güncellenemedi.");
        } finally {
            setStatusLoading(false);
        }
    };

    const handleConvertToInvoice = async () => {
        if (hasInvoice) return toast.warning("Zaten faturalandı.");
        if (order?.account?.isBlocked) return toast.error("Cari Bloke! İşlem yapılamaz.");

        try {
            await store.convertToInvoice(order.id);
            toast.success("Sipariş başarıyla faturalandırıldı.");
            // Faturadan sonra listeyi ve local veriyi yenile
            if (store.fetchDataDynamic) await store.fetchDataDynamic();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Hata oluştu.");
        }
    };

    const handleConvertToTask = async () => {
        if (!order?.approvedAt && !order?.approvedById) {
            return toast.warning("Sipariş onaylı değil. Görev oluşturmadan önce siparişi onaylamalısınız.");
        }
        if (order?.warehouseTask) {
            return toast.warning("Bu sipariş için zaten bir depo görevi oluşturulmuş.");
        }

        try {
            await store.convertToTask(order.id);
            toast.success("Sipariş başarıyla depo görevine dönüştürüldü.");
            if (store.fetchDataDynamic) await store.fetchDataDynamic();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Hata oluştu.");
        }
    };

    const handleCreateCargo = async () => {
        try {
            setCargoLoading(true);
            const result = await createCargoForOrder(order.id);
            toast.success("Kargo fişi oluşturuldu.");
            if (store.fetchDataDynamic) await store.fetchDataDynamic();
            // Local state update if needed, but fetch will update the list which updates props if selected order reference matches
            setOrder({...order, cargoProvider: result.provider, cargoTrackingNo: result.trackingNo, cargoStatus: 'PENDING', cargoBarcodeData: result.barcodeData});
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Kargo fişi oluşturulamadı.");
        } finally {
            setCargoLoading(false);
        }
    };

    const handleUpdateCargoStatus = async () => {
        try {
            setCargoLoading(true);
            const result = await checkCargoStatus(order.id);
            toast.success(`Kargo durumu güncellendi: ${result.status}`);
            if (store.fetchDataDynamic) await store.fetchDataDynamic();
            setOrder({...order, cargoStatus: result.status});
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Durum güncellenemedi.");
        } finally {
            setCargoLoading(false);
        }
    };

    if (!order) return null;

    return (
        <Dialog 
            visible={isOpen} 
            onHide={onClose} 
            header={`Sipariş Detayı: #${order.orderNumber || order.id}`}
            style={{ width: '85vw' }}
            maximized
            modal
            footer={
                <div className="flex justify-between items-center p-3 card2">
                    <div className="text-sm italic flex items-center gap-2  ">
                         <span>Oluşturan: {order.createdBy?.name || 'Sistem'} - {new Date(order.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="flex gap-2">
                        <TextButton children="Kapat" onClick={onClose} className="btn-cancel" />
                        {!order?.warehouseTask && (
                            <IconTextButton 
                                children={store.loading ? "İşleniyor..." : "Göreve Dönüştür"} 
                                icon={Package} 
                                onClick={handleConvertToTask}
                                className="bg-orange-500 hover:bg-orange-600 text-white border-transparent"
                                disabled={store.loading || (!order?.approvedAt && !order?.approvedById)}
                            />
                        )}
                        {!hasInvoice && (
                            <IconTextButton 
                                children={store.loading ? "İşleniyor..." : "Faturaya Dönüştür"} 
                                icon={Receipt} 
                                onClick={handleConvertToInvoice}
                                className="btn-new"
                                disabled={store.loading || order?.account?.isBlocked}
                            />
                        )}
                    </div>
                </div>
            }
        >
            <div className="grid grid-cols-12 gap-6 p-2">
                
                {/* SOL TARAF */}
                <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
                    <div className="card2 shadow-sm border-l-4 border-blue-500">
                        <div className="flex items-center gap-2 mb-3 text-blue-800 font-bold pb-2 border-b">
                            <User size={18} /> Müşteri ve Finansal Durum
                        </div>
                        <p className="font-bold text-lg leading-tight">{order.account?.name}</p>
                        <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                                <span className=" ">Güncel Bakiye:</span>
                                <span className={`font-bold ${balance < 0 ? "text-red-600" : "text-green-600"}`}>
                                    {balance.toLocaleString()} {order.currency || 'TRY'}
                                </span>
                            </div>
                            {order.account?.riskLimitType !== "UNLIMITED" && (
                                <div className={`p-2 rounded-lg border ${isOverLimit ? " border-red-500" : " border-gray-500"}`}>
                                    <div className="flex justify-between items-center mb-1 text-[10px] font-bold uppercase  ">
                                        <span>Risk Limiti</span>
                                        <span>{totalRiskLimit.toLocaleString()} {order.currency || 'TRY'}</span>
                                    </div>
                                    <div className="w-full   rounded-full h-1.5 overflow-hidden">
                                        <div 
                                            className={`h-full ${isOverLimit ? 'bg-red-500' : 'bg-green-500'}`}
                                            style={{ width: `${Math.min(100, (balance / (totalRiskLimit || 1)) * 100)}%` }}
                                        />
                                    </div>
                                    {isOverLimit && <div className="flex items-center gap-1 text-red-600 text-[10px] font-bold mt-1 uppercase"><AlertTriangle size={12} /> Limit Aşımı Mevcut</div>}
                                </div>
                            )}
                            {order.account?.isBlocked && (
                                <div className="flex items-center gap-2 p-2 bg-red-600 text-white rounded-lg animate-pulse">
                                    <ShieldAlert size={16} />
                                    <div className="flex flex-col text-[10px]">
                                        <span className="font-bold uppercase leading-none">Cari Bloke!</span>
                                        <span className="opacity-90 mt-1">{order.account?.blockReason}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card2 shadow-sm border-l-4  ">
                         <div className="flex items-center gap-2 mb-3  font-bold pb-2 border-b">
                            <Truck size={18} /> Sevkiyat Bilgileri
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold   uppercase">Depo</span>
                                <span className="text-sm font-semibold flex items-center gap-1 mt-1"><Warehouse size={14} className="text-blue-500" />{order.warehouse?.name || 'Belirtilmedi'}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold   uppercase">Teslimat Tarihi</span>
                                <span className="text-sm font-semibold flex items-center gap-1 mt-1"><Calendar size={14} className="text-orange-500" />{order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : 'Belirtilmedi'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="card2 shadow-sm border-l-4 border-orange-500">
                        <div className="flex items-center gap-2 mb-3 font-bold border-b pb-2 text-orange-800">
                            <Truck size={18} /> Kargo Entegrasyon
                        </div>
                        <div className="flex flex-col gap-3">
                            {!order.cargoTrackingNo ? (
                                <div>
                                    <p className="text-xs text-gray-500 mb-2">Bu sipariş için henüz kargo fişi oluşturulmamış.</p>
                                    <IconTextButton 
                                        children={cargoLoading ? "Oluşturuluyor..." : "Kargo Fişi Oluştur"} 
                                        icon={Package} 
                                        onClick={handleCreateCargo}
                                        className="btn-new w-full justify-center"
                                        disabled={cargoLoading || !order.approvedAt}
                                    />
                                    {!order.approvedAt && <p className="text-[10px] text-red-500 mt-1">Sipariş onaylanmadan kargo oluşturulamaz.</p>}
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                                        <span className="text-[10px] font-bold uppercase text-gray-500">Firma</span>
                                        <span className="font-bold text-sm">{order.cargoProvider}</span>
                                    </div>
                                     <div className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                                         <span className="text-[10px] font-bold uppercase text-gray-500">Takip No</span>
                                         {(() => {
                                             const isReal = Boolean(
                                                 order.cargoTrackingNo &&
                                                 String(order.cargoTrackingNo).trim() !== String(order.orderNumber || '').trim() &&
                                                 String(order.cargoTrackingNo).trim() !== String(order.cargoIntegrationNo || '').trim() &&
                                                 String(order.cargoTrackingNo).trim() !== String(order.id || '').trim()
                                             );
                                             if (isReal) {
                                                 return (
                                                     <div className="flex items-center gap-1.5">
                                                         <span className="font-bold text-sm text-blue-600">{order.cargoTrackingNo}</span>
                                                         <button
                                                             type="button"
                                                             onClick={() => {
                                                                 navigator.clipboard.writeText(String(order.cargoTrackingNo));
                                                                 toast.success(`Kargo takip no kopyalandı: ${order.cargoTrackingNo}`);
                                                             }}
                                                             className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                             title="Takip Numarasını Kopyala"
                                                         >
                                                             <Copy size={13} />
                                                         </button>
                                                     </div>
                                                 );
                                             }
                                             return (
                                                 <span className="text-xs font-medium text-orange-500">Kargoda Bekliyor (Henüz Takip No Oluşmadı)</span>
                                             );
                                         })()}
                                     </div>
                                    <div className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                                        <span className="text-[10px] font-bold uppercase text-gray-500">Durum</span>
                                        <span className="font-bold text-sm text-orange-600">{order.cargoStatus}</span>
                                    </div>

                                    <div className="flex gap-2 mt-2">
                                        <button 
                                            className="flex-1 flex items-center justify-center gap-1 p-2 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors text-xs font-bold"
                                            onClick={handleUpdateCargoStatus}
                                            disabled={cargoLoading}
                                        >
                                            <RefreshCcw size={14} className={cargoLoading ? "animate-spin" : ""} />
                                            Durumu Güncelle
                                        </button>
                                        
                                        {order.cargoBarcodeData && (
                                            <button 
                                                className="flex-1 flex items-center justify-center gap-1 p-2 rounded bg-gray-800 text-white hover:bg-gray-700 transition-colors text-xs font-bold"
                                                onClick={() => {
                                                    // TODO: Send cargoBarcodeData to local print service or show in a modal
                                                    toast.info("Barkod yazdırma işlemi başlatılıyor...");
                                                    console.log(order.cargoBarcodeData);
                                                }}
                                            >
                                                <Printer size={14} />
                                                Barkod Yazdır
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card2 shadow-sm border-l-4 border-emerald-500">
                        <div className="flex items-center gap-2 mb-3 font-bold border-b pb-2 text-emerald-800">
                            <RefreshCcw size={18} className={statusLoading ? "animate-spin" : ""} /> Durum Yönetimi
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-[10px] font-bold uppercase  ">Durum Güncelle</label>
                            <Dropdown 
                                value={currentStatusValue}
                                options={filteredOptions}
                                optionLabel="name"
                                placeholder="Yeni Durum Seçin..."
                                className="w-full"
                                disabled={statusLoading || filteredOptions.length === 0}
                                onChange={(e) => handleUpdateStatus(e.value)}
                                itemTemplate={(option) => (
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: option.color }} />
                                        <span>{option.name}</span>
                                    </div>
                                )}
                            />
                            <div className="flex items-center gap-2 mt-2">
                                <span className="text-[10px] font-bold  uppercase tracking-tight">Aktif Durum:</span>
                                <Tag 
                                    value={order.statuses?.[0]?.name || "Bekliyor"} 
                                    style={{ backgroundColor: order.statuses?.[0]?.color || '#94a3b8' }} 
                                />
                            </div>
                        </div>
                        {hasInvoice && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg mt-3">
                                <div className="flex items-center gap-2 text-green-700 font-bold text-sm mb-1"><CheckCircle2 size={16} /> Sipariş Faturalandırıldı</div>
                                <div className="text-[11px] text-green-600"><span className="font-bold uppercase">Fatura No:</span> {invoiceInfo?.number}</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* SAĞ TARAF: Sipariş Kalemleri */}
                <div className="col-span-12 lg:col-span-8 overflow-hidden shadow-sm card p-0 flex flex-col">
                    <div className="card2 flex justify-between items-center border-b rounded-none bg-gray-50/50 p-4">
                        <div className="flex items-center gap-2 font-bold text-blue-900"><Package size={18} /> Sipariş Kalemleri</div>
                        <div className="text-[11px] font-bold px-3 py-1 bg-blue-100 text-blue-700 rounded-full">{order.items?.length || 0} Kalem Ürün</div>
                    </div>
                    
                    <DataTable value={order.items} className="p-datatable-sm flex-1" responsiveLayout="scroll" scrollHeight="flex">
                        <Column header="Ürün Bilgisi" body={(row) => (
                            <div className="flex flex-col">
                                <span className="font-bold text-sm">{row.stock?.name}</span>
                                <span className="text-[10px]  font-mono">SKU: {row.stock?.sku || '---'}</span>
                            </div>
                        )} />
                        <Column field="quantity" header="Miktar" align="center" body={(row) => <span className="font-bold  ">{row.quantity}</span>} />
                        <Column header="Birim Fiyat" body={(row) => `${row.price.toLocaleString()} ${order.currency || 'TRY'}`} align="right" />
                        <Column header="İndirim" align="right" body={(row) => (
                            (row.discountAmount > 0 || row.discountRate > 0)
                                ? <div className="flex flex-col items-end">
                                    <span className="text-red-500 font-bold text-xs">%{row.discountRate}</span>
                                    <span className="text-[10px] text-gray-500">{(row.discountAmount || 0).toLocaleString()} {order.currency || 'TRY'}</span>
                                  </div>
                                : <span className="text-gray-400">-</span>
                        )} />
                        <Column header="Toplam" body={(row) => <span className="font-bold text-blue-700">{(row.total).toLocaleString()} {order.currency || 'TRY'}</span>} align="right" />
                    </DataTable>

                    <div className="p-6   flex justify-between items-center">
                        <div className="flex items-center gap-2 ">
                            <Info size={16} />
                            <span className="text-[10px] uppercase font-bold tracking-widest">Fiyatlara KDV Dahildir</span>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            {(() => {
                                const totalDiscount = order.items?.reduce((sum: number, item: any) => sum + (item.discountAmount || 0), 0) || 0;
                                const bulkDiscount = order.orderLevelBulkDiscount || 0;
                                const totalDis = totalDiscount + bulkDiscount;
                                if (totalDis > 0) {
                                    return (
                                        <div className="flex items-center gap-2 text-red-500 border-b border-red-100 pb-1 w-full justify-end">
                                            <span className="text-[10px] font-bold uppercase">Toplam İndirim:</span>
                                            <span className="font-bold text-sm">-{totalDis.toLocaleString()} {order.currency || 'TRY'}</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            <div className="flex flex-col items-end mt-1">
                                <span className="text-[11px] font-bold   uppercase">Ödenecek Tutar</span>
                                <div className="flex items-baseline gap-1   font-black  ">
                                    <span>{orderAmount.toLocaleString()}</span>
                                    <span className="text-sm">{order.currency || 'TRY'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};